<script>
import { mapGetters, mapActions } from "vuex";
import EmptyState from "@/components/EmptyState.vue";
import TableSkeleton from "@/components/TableSkeleton.vue";
import sizeVue from "@/mixins/size.vue";
import axios from "axios";

export default {
  components: { EmptyState, TableSkeleton },
  props: {
    value: Number,
    isFiltering: Boolean,
    filters: Object,
  },

  data: () => ({
    isLoading: false,
    search: null,
    options: {},
    totalItemsCount: 0,
    itemsPerPage: 20,
    page: 1,
    isExport: false,
    selectAll: false,
    selectedRows: [],
    isDeleteDialog: false,
    isRestoreDialog: false,
    restoreLoading: false,
    isMoveDialog: false,
    moveActionLoading: false,
    canExport: false,
    canDelete: false,
    canUpdate: false,
    tableSettingsOpen: false,
    visibleColumnKeys: [],
  }),

  async mounted() {
    const canRead = await this.hasRole("KETGAN_24_25", "READ");
    if (!canRead) { this.$router.replace("/"); return; }
    this.canExport = await this.hasRole("KETGAN_24_25", "EXCEL");
    this.canDelete = await this.hasRole("KETGAN_24_25", "DELETE");
    this.canUpdate = await this.hasRole("KETGAN_24_25", "UPDATE");
    this._onKeyScroll = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const wrapper = this.$el.querySelector('.v-data-table__wrapper');
        if (wrapper) { e.preventDefault(); wrapper.scrollLeft += e.key === 'ArrowRight' ? 200 : -200; }
      }
    };
    document.addEventListener('keydown', this._onKeyScroll);
  },
  beforeDestroy() {
    if (this._onKeyScroll) document.removeEventListener('keydown', this._onKeyScroll);
  },

  mixins: [sizeVue],

  created() {
    const defaults = this.defaultColumnKeys;
    try {
      const saved = JSON.parse(localStorage.getItem("dropped2024.table.visibleColumns"));
      this.visibleColumnKeys = Array.isArray(saved) && saved.length ? saved : defaults;
    } catch {
      this.visibleColumnKeys = defaults;
    }
  },

  computed: {
    defaultColumnKeys() {
      return this.allColumnsDef.map(c => c.key);
    },
    allColumnsDef() {
      return [
        { key: "fullName", label: this.$t("Full name") },
        { key: "passportNumber", label: this.$t("Passport") },
        { key: "contractNumber", label: this.$t("Contract Number") },
        { key: "groupCode", label: this.$t("Group code") },
        { key: "mobilePhone", label: this.$t("Phone") },
        { key: "kurs", label: this.$t("Course") },
        { key: "educationTypeName", label: this.$t("Education type") },
        { key: "educationFacultyName", label: this.$t("Faculty") },
        { key: "regionName", label: this.$t("Region") },
        { key: "cityName", label: this.$t("City") },
        { key: "paidStatus", label: this.$t("Paid status") },
        { key: "totalPaidSumma", label: this.$t("Paid sum") },
        { key: "totalOstatok", label: this.$t("Ostatok") },
        { key: "id", label: this.$t("ID") },
        { key: "contractDate", label: this.$t("Contract Date") },
        { key: "totalSumma", label: this.$t("Total Sum") },
        { key: "totalSummaWord", label: this.$t("Total Sum (word)") },
        { key: "facultyCode", label: this.$t("Faculty Code") },
        { key: "applicationId", label: this.$t("Application ID") },
        { key: "firstName", label: this.$t("First Name") },
        { key: "lastName", label: this.$t("Last Name") },
        { key: "middleName", label: this.$t("Middle Name") },
        { key: "birthDate", label: this.$t("Birth Date") },
        { key: "gender", label: this.$t("Gender") },
        { key: "disability", label: this.$t("Disability") },
        { key: "disabilityNote", label: this.$t("Disability Note") },
        { key: "country", label: this.$t("Country") },
        { key: "addressLine", label: this.$t("Address") },
        { key: "citizenship", label: this.$t("Citizenship") },
        { key: "nationality", label: this.$t("Nationality") },
        { key: "pinflNumber", label: this.$t("PINFL") },
        { key: "fatherPhone", label: this.$t("Father Phone") },
        { key: "motherPhone", label: this.$t("Mother Phone") },
        { key: "homePhone", label: this.$t("Home Phone") },
        { key: "schoolType", label: this.$t("School Type") },
        { key: "schoolRegion", label: this.$t("School Region") },
        { key: "schoolCity", label: this.$t("School City") },
        { key: "schoolName", label: this.$t("School Name") },
        { key: "graduationYear", label: this.$t("Graduation Year") },
        { key: "highSchoolDiplomaNumber", label: this.$t("Diploma Number") },
        { key: "foreignLanguageProficiency", label: this.$t("Foreign Language Proficiency") },
        { key: "certificateOther", label: this.$t("Certificate Other") },
        { key: "score", label: this.$t("Score") },
        { key: "certificateNumber", label: this.$t("Certificate Number") },
        { key: "educationLanguage", label: this.$t("Education Language") },
        { key: "currentPayment", label: this.$t("Current Payment") },
        { key: "paymentDate", label: this.$t("Payment Date") },
        { key: "passedDate", label: this.$t("Passed Date") },
        { key: "converted", label: this.$t("Converted") },
        { key: "typeAbiturient", label: this.$t("Type Abiturient") },
        { key: "promotion", label: this.$t("Promotion") },
        { key: "usatPassword", label: this.$t("USAT Password") },
        { key: "mvdir", label: this.$t("Mvdir") },
      ];
    },
    visibleColumns() {
      const map = new Map(this.allColumnsDef.map((c) => [c.key, c]));
      return this.visibleColumnKeys.map((k) => map.get(k)).filter(Boolean);
    },
    dynamicHeaders() {
      const base = [{ text: "", value: "checkbox", sortable: false }];
      const dynamic = this.visibleColumns.map((c) => ({
        text: c.label,
        value: c.key,
      }));
      return [...base, ...dynamic];
    },

    tableHeight: (vm) =>
      vm.size.width >= 1200 ? vm.size.height - 370 : "auth",

    ...mapGetters("admin-dropped2024", ["GET_APPLICATIONS", "INIT_FACULTIES"]),
    items() {
      return this.GET_APPLICATIONS;
    },

    ...mapGetters("auth", ["GET_USER"]),
    user() {
      return this.GET_USER;
    },

    isSupperAdmin() {
      return this.user.role === "ADMIN_SUPER";
    },

    tab: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },

    paginationCount() {
      return Math.ceil(this.totalItemsCount / this.itemsPerPage);
    },

    displayItems() {
      return (this.items || []).map(this.mapItem);
    },

    filteredItems() {
      return this.displayItems.filter(this.shouldShowRow);
    },

    filteredItemIds() {
      return this.filteredItems.map((item) => item.id);
    },
  },

  watch: {
    visibleColumnKeys: {
      handler(val) { localStorage.setItem("dropped2024.table.visibleColumns", JSON.stringify(val)); },
      deep: true,
    },
    filters: {
      handler() {
        this.page = 1;
        this.getItems();
      },
      deep: true,
    },
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
    tab() {
      this.page = 1;
      this.$nextTick(() => {
        this.getItems();
      });
    },
    search() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.getItems(), 400);
    },

    isLoading(v) {
      this.$emit("loading", v);
    },
    selectAll(val) {
      // Pure flag-based: when ON, all rows across all pages are conceptually
      // selected; the row-level :input-value reads selectAll directly so we
      // don't materialize every id into selectedRows.
      if (!val) this.selectedRows = [];
    },
  },

  methods: {
    toggleRow(id) {
      if (this.selectAll) {
        this.selectAll = false;
        this.selectedRows = [id];
        return;
      }
      const idx = this.selectedRows.indexOf(id);
      if (idx >= 0) this.selectedRows.splice(idx, 1);
      else this.selectedRows.push(id);
    },

    toggleAllColumns() {
      const all = this.defaultColumnKeys;
      this.visibleColumnKeys = this.visibleColumnKeys.length === all.length ? [] : [...all];
    },
    resetColumns() {
      this.visibleColumnKeys = [...this.defaultColumnKeys];
    },

    mapItem(i) {
      const phone =
        this.$tel && i.mobilePhone ? this.$tel(i.mobilePhone) : (i.mobilePhone || "");
      return {
        ...i,
        fullName: this.getFullName(i),
        educationTypeName: i.educationType?.name || "",
        educationFacultyName: i.educationFaculty?.name || "",
        regionName: i.region?.name || "",
        cityName: i.city?.name || "",
        mobilePhone: phone,
      };
    },

    formatCurrency(value) {
      if (value == null) return "";
      return new Intl.NumberFormat("uz-UZ", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value) + " so'm";
    },

    getCell(item, key) {
      switch (key) {
        case "fullName":
          return item.fullName || this.getFullName(item);
        case "passportNumber":
          return item.passportNumber || "";
        case "contractNumber":
          return item.contractNumber || "";
        case "groupCode":
          return item.groupCode || "";
        case "mobilePhone":
          return item.mobilePhone || "";
        case "kurs":
          return this.getKurs(item);
        case "educationTypeName":
          return item.educationTypeName || item.educationType?.name || "";
        case "educationFacultyName":
          return item.educationFacultyName || item.educationFaculty?.name || "";
        case "regionName":
          return item.regionName || item.region?.name || "";
        case "cityName":
          return item.cityName || item.city?.name || "";
        case "paidStatus":
          return item.paidStatus || "";
        case "totalPaidSumma":
          return this.formatCurrency(item.totalPaidSumma);
        case "totalOstatok":
          return this.formatCurrency(item.totalOstatok);
        default:
          // Lookup columns arrive as { id, name }; show the name, never the raw object.
          {
            const v = item && key in item ? item[key] ?? "" : "";
            if (v === null || typeof v !== "object") return v;
            return Array.isArray(v)
              ? v.map((x) => (x && typeof x === "object" ? x.name : x)).join(", ")
              : v.name ?? "";
          }
      }
    },

    ...mapActions(
      "admin-dropped2024",
      ["INIT_APPLICATIONS", "INIT_FILTERED_APPLICATIONS", "INIT_BY_ID", "EXPORT_EXCEL", "EXPORT_EXCEL_BY_FILTER", "DELETE_APPLICATION", "DELETE_ALL", "MOVE_TO_2025", "MOVE_ALL_TO_2025", "MOVE_BY_IDS_TO_2025", "FETCH_FILTERED_IDS", "RESTORE_BY_IDS"]
    ),

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },

    updateIsFiltering(newVal) {
      this.$emit('update:isFiltering', newVal);
    },

    getItems() {
      const f = this.filters || {};
      const filtering =
        (f.kurs?.length ?? 0) > 0 ||
        (f.selectedEducationFaculties?.length ?? 0) > 0 ||
        (f.selectedEducationLanguages?.length ?? 0) > 0 ||
        (f.selectedEducationTypes?.length ?? 0) > 0 ||
        (f.selectedDiscounts?.length ?? 0) > 0 ||
        (f.selectedDistricts?.length ?? 0) > 0 ||
        (f.selectedGenders?.length ?? 0) > 0 ||
        (f.selectedGroups?.length ?? 0) > 0 ||
        !!f.noGroup ||
        (f.selectedPaymentPercents?.length ?? 0) > 0 ||
        (f.selectedDocumentStatus?.length ?? 0) > 0 ||
        (f.selectedRegions?.length ?? 0) > 0 ||
        (f.selectedRole?.length ?? 0) > 0;

      this.$emit('update:isFiltering', filtering);

      if (this.isLoading) return;
      this.isLoading = true;
      this.options.status = this.tab;
      this.options.search = this.search;

      const finish = (totalItemsCount) => {
        this.totalItemsCount = totalItemsCount;
        this.isLoading = false;
      };

      if (filtering) {
        this.INIT_FILTERED_APPLICATIONS({
          filters: this.filters,
          options: this.options,
        }).then(finish);
      } else {
        this.INIT_APPLICATIONS(this.options).then(finish);
      }
    },

    onExport() {
      this.isExport = true;
      const f = this.filters || {};
      const filtering =
        (f.kurs?.length ?? 0) > 0 ||
        (f.selectedEducationFaculties?.length ?? 0) > 0 ||
        (f.selectedEducationLanguages?.length ?? 0) > 0 ||
        (f.selectedEducationTypes?.length ?? 0) > 0 ||
        (f.selectedDiscounts?.length ?? 0) > 0 ||
        (f.selectedDistricts?.length ?? 0) > 0 ||
        (f.selectedGenders?.length ?? 0) > 0 ||
        (f.selectedGroups?.length ?? 0) > 0 ||
        !!f.noGroup ||
        (f.selectedPaymentPercents?.length ?? 0) > 0 ||
        (f.selectedDocumentStatus?.length ?? 0) > 0 ||
        (f.selectedRegions?.length ?? 0) > 0 ||
        (f.selectedRole?.length ?? 0) > 0;

      const p = filtering
        ? this.EXPORT_EXCEL_BY_FILTER({ filters: this.filters, status: this.tab })
        : this.EXPORT_EXCEL({ status: this.tab });

      p.finally(() => {
        this.isExport = false;
      });
    },

    getFullName(item) {
      const lastName = item.lastName || "";
      const firstName = item.firstName || "";
      const middleName = item.middleName || "";
      return `${lastName} ${firstName} ${middleName}`.trim();
    },

    getKurs(item) {
      return item.kurs ? `${item.kurs} kurs` : "";
    },

    async onOpen({ id }) {
      const status = await this.INIT_BY_ID({ id });
      if (status) {
        this.$router.push(`/contract_2024_2025-show/${id}`);
      }
    },

    onClear() {
      this.search = null;
      this.$nextTick(() => {
        this.getItems();
      });
    },

    shouldShowRow(item) {
      const hasFullName =
        (item.firstName && item.firstName.trim()) ||
        (item.lastName && item.lastName.trim()) ||
        (item.middleName && item.middleName.trim());

      return (
        hasFullName ||
        (item.passportNumber && item.passportNumber.trim()) ||
        (item.contractNumber && item.contractNumber.trim()) ||
        (item.groupCode && item.groupCode.trim()) ||
        (item.mobilePhone && item.mobilePhone.trim()) ||
        item.kurs
      );
    },

    // ===== Restore-to-studies handlers =====
    confirmRestore() {
      this.isRestoreDialog = true;
    },

    async restoreSelected() {
      if (this.restoreLoading) return;
      this.restoreLoading = true;
      try {
        if (this.selectAll) {
          const filters = this.isFiltering ? this.filters : null;
          const ids = await this.FETCH_FILTERED_IDS({
            filters,
            status: this.tab,
            search: this.search,
          });
          if (ids.length) await this.RESTORE_BY_IDS(ids);
        } else {
          await Promise.all(
            this.selectedRows.map((id) => axios.put(`/admin/contract2024/dropout/restore/${id}`))
          );
        }
        this.selectedRows = [];
        this.selectAll = false;
        this.getItems && this.getItems();
        this.$emit("reload");
        this.isRestoreDialog = false;
        this.$store.commit("SET_SNACKBAR", {
          text: this.$t("Selected students restored to studies"),
          color: "success",
        });
      } catch (err) {
        this.$store.commit("SET_SNACKBAR", {
          text: this.$t("Error restoring students"),
          color: "error",
        });
      } finally {
        this.restoreLoading = false;
      }
    },

    confirmDelete() {
      this.isDeleteDialog = true;
    },

    async deleteSelected() {
      const finish = (totalItemsCount) => {
        this.totalItemsCount = totalItemsCount;
      };

      if (this.selectAll) {
        const f = this.filters || {};
        const filtering =
          (f.kurs?.length ?? 0) > 0 ||
          (f.selectedEducationFaculties?.length ?? 0) > 0 ||
          (f.selectedEducationLanguages?.length ?? 0) > 0 ||
          (f.selectedEducationTypes?.length ?? 0) > 0 ||
          (f.selectedDiscounts?.length ?? 0) > 0 ||
          (f.selectedDistricts?.length ?? 0) > 0 ||
          (f.selectedGenders?.length ?? 0) > 0 ||
          (f.selectedGroups?.length ?? 0) > 0 ||
          !!f.noGroup ||
          (f.selectedPaymentPercents?.length ?? 0) > 0 ||
          (f.selectedDocumentStatus?.length ?? 0) > 0 ||
          (f.selectedRegions?.length ?? 0) > 0 ||
          (f.selectedRole?.length ?? 0) > 0;

        const filters = filtering ? this.filters : null;
        const ids = await this.FETCH_FILTERED_IDS({
          filters,
          status: this.tab,
          search: this.search,
        });
        this.selectedRows = [];
        this.selectAll = false;
        this.page = 1;
        this.isDeleteDialog = false;
        if (ids.length) {
          await Promise.all(ids.map((id) => this.DELETE_APPLICATION({ id })));
        }
        this.$emit("reload");
        await this.INIT_APPLICATIONS(this.options).then(finish);
        this.$store.dispatch("snackbar/SHOW_SNACKBAR", {
          text: this.$t("Selected students deleted successfully"),
          color: "success",
        });
        return;
      }

      const promises = this.selectedRows.map((id) =>
        this.DELETE_APPLICATION({ id })
      );
      await Promise.all(promises);
      this.selectedRows = [];
      this.selectAll = false;
      this.isDeleteDialog = false;
      this.getItems();
      this.$emit("reload");
      this.$store.dispatch("snackbar/SHOW_SNACKBAR", {
        text: this.$t("Selected students deleted successfully"),
        color: "success",
      });
    },

    confirmMove() {
      this.isMoveDialog = true;
    },

    async moveSelected() {
      if (this.moveActionLoading) return;

      this.moveActionLoading = true;
      if (this.selectAll) {
        const f = this.filters || {};
        const filtering =
          (f.kurs?.length ?? 0) > 0 ||
          (f.selectedEducationFaculties?.length ?? 0) > 0 ||
          (f.selectedEducationLanguages?.length ?? 0) > 0 ||
          (f.selectedEducationTypes?.length ?? 0) > 0 ||
          (f.selectedDiscounts?.length ?? 0) > 0 ||
          (f.selectedDistricts?.length ?? 0) > 0 ||
          (f.selectedGenders?.length ?? 0) > 0 ||
          (f.selectedGroups?.length ?? 0) > 0 ||
          !!f.noGroup ||
          (f.selectedPaymentPercents?.length ?? 0) > 0 ||
          (f.selectedDocumentStatus?.length ?? 0) > 0 ||
          (f.selectedRegions?.length ?? 0) > 0 ||
          (f.selectedRole?.length ?? 0) > 0;

        const filters = filtering ? this.filters : null;
        const ids = await this.FETCH_FILTERED_IDS({
          filters,
          status: this.tab,
          search: this.search,
        });
        if (ids.length) {
          await this.MOVE_BY_IDS_TO_2025(ids);
        }
      } else {
        const promises = this.selectedRows.map((id) =>
          this.MOVE_TO_2025(id)
        );
        await Promise.all(promises);
      }

      this.selectedRows = [];
      this.selectAll = false;
      this.getItems();
      this.isMoveDialog = false;

      this.$store.dispatch("snackbar/SHOW_SNACKBAR", {
        text: this.$t("Selected students moved to 2025 successfully"),
        color: "success",
      });

      this.$emit("reload");
      this.moveActionLoading = false;
    },

    
  },
}
</script>

<template>
  <div>
    <!-- ── Toolbar: Search (left) + Actions (right) ── -->
    <div class="toolbar-row">
      <v-text-field
        :placeholder="$t('Izlash...')"
        append-icon="mdi-magnify"
        v-model="search"
        dense
        hide-details
        outlined
        clearable
        class="search-input"
        @click:append="getItems"
        @keyup.enter="getItems"
        @click:clear="onClear"
      />

      <div class="circle-actions">
        <v-chip class="badge-count" color="primary" small>
          {{ totalItemsCount || 0 }}
        </v-chip>

        <v-btn
          :loading="isExport"
          v-if="canExport"
          class="ml-2 modern-button modern-export"
          :disabled="excelExportDisabled"
          @click="onExport"
        >
          <img src="/icons/excel-icon.svg" alt="Excel" class="export-icon">
          EXPORT
        </v-btn>

        <div v-if="(selectAll || selectedRows.length)" class="upper-btn-group">
          <v-btn
              v-if="canDelete && (selectAll || selectedRows.length)"
              class="font-made upper-btn ml-2 modern-button modern-danger"
              @click="confirmDelete"
            >
              <v-icon left size="20" color="#fff">mdi-trash-can</v-icon>
              {{ $t("Delete Selected") }}
            </v-btn>

          <v-btn
            v-if="canUpdate && (selectAll || selectedRows.length)"
            class="ml-2 modern-button modern-success"
            @click="confirmRestore"
          >
            <v-icon left size="20" color="#fff">mdi-account-reactivate</v-icon>
            {{ $t("O'qishga qaytarish") }}
          </v-btn>

          <v-btn
            v-if="selectAll || selectedRows.length"
            class="ml-2 modern-button modern-primal"
            @click="confirmMove"
          >
            <v-icon left small>mdi-arrow-right</v-icon>
            {{ $t("Move to 2025") }}
          </v-btn>
        </div>

        <!-- Columns menu -->
        <v-menu v-model="tableSettingsOpen" offset-y left :close-on-content-click="false" :allow-overflow="true">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="ml-2 modern-button font-made" v-bind="attrs" v-on="on">
              <img src="/icons/column-list-icon.svg" alt="Columns" style="width:13px;height:13px;opacity:0.6;margin-right:6px" :style="$vuetify.theme.dark ? 'filter:brightness(0) invert(1);opacity:0.7' : ''">
              {{ $t("Columns") }}
            </v-btn>
          </template>
          <v-card class="columns-menu-card">
            <v-list dense class="columns-menu-list">
              <v-subheader>{{ $t("Show/Hide Columns") }}</v-subheader>
              <v-list-item>
                <v-btn small text class="mr-2" @click="toggleAllColumns"><v-icon left small>mdi-select-all</v-icon>{{ $t("Select all") }}</v-btn>
                <v-btn small text @click="resetColumns"><v-icon left small>mdi-undo</v-icon>{{ $t("Reset defaults") }}</v-btn>
              </v-list-item>
              <v-divider class="my-1" />
              <v-list-item v-for="col in allColumnsDef" :key="col.key" class="columns-menu-item">
                <v-checkbox dense hide-details :label="col.label" :value="col.key" v-model="visibleColumnKeys" />
              </v-list-item>
            </v-list>
            <div class="columns-menu-footer">
              <v-btn small class="columns-apply-btn" @click="tableSettingsOpen = false">{{ $t("Apply") }}</v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>

    <v-data-table
      :mobile-breakpoint="0"
      :height="tableHeight"
      :loading="isLoading"
      hide-default-footer
      :headers="dynamicHeaders"
      fixed-header
      class="modern-table elevation-0"
      :items="filteredItems"
      :options.sync="options"
      :server-items-length="totalItemsCount"
      item-key="id"
      :items-per-page="itemsPerPage"
      :page.sync="page"
    >
      <template v-slot:header.checkbox>
        <v-checkbox
          dense
          hide-details
          :input-value="selectAll"
          @change="selectAll = !selectAll"
        />
      </template>

      <template v-slot:body="props">
        <tbody>
          <tr v-for="item in props.items" :key="item.id" class="modern-row">
            <td @click.stop>
              <v-simple-checkbox
                :ripple="false"
                :value="selectAll || selectedRows.includes(item.id)"
                @input="toggleRow(item.id)"
              />
            </td>

            <td
              v-for="col in visibleColumns"
              :key="col.key"
              @click="onOpen(item)"
            >
              {{ getCell(item, col.key) }}
            </td>
          </tr>
          <tr v-if="isLoading && !props.items.length">
            <td :colspan="props.headers.length">
              <table-skeleton :cols="props.headers.length" />
            </td>
          </tr>
          <tr v-else-if="!props.items.length">
            <td :colspan="props.headers.length">
              <empty-state :title="$t('No results found')" />
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <div class="pt-2 pb-2">
      <v-pagination :total-visible="10" v-model="page" :length="paginationCount" />
    </div>

    <v-dialog v-model="isDeleteDialog" max-width="500px">
      <v-card class="modern-modal">
        <v-card-title class="text-h6 modern-modal-title">
          {{ $t("Confirm Delete") }}
        </v-card-title>
        <v-card-text class="modern-modal-text">
          {{ $t("Are you sure you want to delete selected students?") }}
        </v-card-text>
        <v-card-actions class="modern-modal-actions">
          <v-spacer />
          <v-btn text class="cancel-button" @click="isDeleteDialog = false">
            <v-icon left small>mdi-close</v-icon>
            {{ $t("Cancel") }}
          </v-btn>
          <v-btn color="error" class="modern-button modern-danger" @click="deleteSelected">
            <v-icon left small>mdi-check</v-icon>
            {{ $t("Delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isMoveDialog" max-width="500px">
      <v-card class="modern-modal">
         <v-progress-linear
          v-if="moveActionLoading"
          indeterminate
          color="primary"
          height="3"
          class="modal-loader"
        />

        <v-card-title class="text-h6 modern-modal-title">
          {{ $t("Confirm Move") }}
        </v-card-title>
        <v-card-text class="modern-modal-text">
          {{ $t("Are you sure you want to move selected students to 2025?") }}
        </v-card-text>
        <v-card-actions class="modern-modal-actions">
          <v-spacer />
          <v-btn text class="cancel-button" @click="isMoveDialog = false">
            <v-icon left small>mdi-close</v-icon>
            {{ $t("Cancel") }}
          </v-btn>
          <v-btn color="primary" class="modern-button modern-primary" @click="moveSelected">
            <v-icon left small>mdi-check</v-icon>
            {{ $t("Move") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isRestoreDialog" max-width="500px">
      <v-card class="modern-modal">
        <v-progress-linear
          v-if="restoreLoading"
          indeterminate
          color="success"
          height="3"
          class="modal-loader"
        />

        <v-card-title class="text-h6 modern-modal-title">
          {{ $t("Confirm Restore") }}
        </v-card-title>
        <v-card-text class="modern-modal-text">
          {{ $t("Are you sure you want to restore selected students to studies?") }}
        </v-card-text>
        <v-card-actions class="modern-modal-actions">
          <v-spacer />
          <v-btn text class="cancel-button" @click="isRestoreDialog = false">
            <v-icon left small>mdi-close</v-icon>
            {{ $t("Cancel") }}
          </v-btn>
          <v-btn class="modern-button modern-success" @click="restoreSelected">
            <v-icon left small>mdi-check</v-icon>
            {{ $t("Confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
/* ── Toolbar Row ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px 12px;
}

.toolbar-row > .search-input {
  flex: 1;
  min-width: 0;
}

.circle-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  max-width: 100%;
  flex-wrap: wrap;
  row-gap: 10px;
}

.circle-actions > * {
  flex-shrink: 0;
}

/* ── Search Input ── */
.search-input {
  max-width: none;
}

::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #d0d5dd !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #00274c !important;
    border-width: 1.5px !important;
  }
  .v-icon {
    color: #94a3b8 !important;
    font-size: 20px !important;
  }
  input {
    font-size: 14px;
  }
  input::placeholder {
    color: #94a3b8 !important;
  }
}

::v-deep .search-input.theme--dark {
  .v-input__slot {
    background: #22232e !important;
  }
  fieldset {
    border-color: transparent !important;
  }
  &:hover fieldset {
    border-color: #33354a !important;
  }
  &.v-input--is-focused fieldset {
    border-color: #ffd451 !important;
  }
  .v-icon {
    color: #606275 !important;
  }
  input {
    color: #d5d6de !important;
  }
  input::placeholder {
    color: #606275 !important;
  }
}

.modern-button {
  text-transform: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.modern-primary {
  background: #1D6F42!important;
  color: #fff;
}

.modern-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #217346 !important;
  color: #fff !important;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
  min-width: 130px !important;
}
.modern-export:hover {
  background: #1a5c38 !important;
}
.modern-export:active {
  transform: scale(.97);
}
.export-icon {
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  margin-right: 10px;
}

.modern-danger {
  background: linear-gradient(90deg, #c62828, #ef5350);
  color: #fff;
}

.modern-primal {
  background: #0a2749!important;
  color: #fff;
}

.modern-success {
  background: #16a34a !important;
  border: 1px solid #15803d !important;
  color: #fff !important;
  transition: background-color 0.2s ease;
}
.modern-success:hover {
  background: #15803d !important;
}
.modern-success:active {
  background: #166534 !important;
  transform: scale(0.97);
}

.cancel-button {
  color: #555;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.modern-table {
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
}

.modern-row:hover {
  background-color: #f5f5f5;
  cursor: pointer;
}

.modern-modal {
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.modern-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modern-modal-text {
  font-size: 16px;
  padding: 12px 0;
}

.modern-modal-actions {
  padding-top: 12px;
}

// Table height adjustment
::v-deep .v-data-table--fixed-height .v-data-table__wrapper,
::v-deep .v-data-table--fixed-header .v-data-table__wrapper {
  height: auto    !important;
  max-height: none!important;
}

.columns-menu-card {
  min-width: 260px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
}
.columns-menu-list {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 0 !important;
}
.columns-menu-item {
  padding: 0 12px !important;
  min-height: 32px !important;
}
.columns-menu-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: inherit;
  flex-shrink: 0;
}
.columns-apply-btn.v-btn {
  background-color: #ffd451 !important;
  color: #111318 !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-radius: 8px !important;
}

.badge-count {
  font-size: 12px !important;
  padding: 4px 10px !important;
  font-weight: 600 !important;
}
.badge-count.primary {
  color: white !important;
}
.theme--dark .badge-count.primary {
  color: #111318 !important;
}



/* Sticky checkbox + F.I.O columns */
::v-deep .v-data-table__wrapper th:nth-child(1),
::v-deep .v-data-table__wrapper td:nth-child(1) {
  position: sticky !important;
  left: 0 !important;
  z-index: 3 !important;
}
::v-deep .v-data-table__wrapper th:nth-child(2),
::v-deep .v-data-table__wrapper td:nth-child(2) {
  position: sticky !important;
  left: 48px !important;
  z-index: 3 !important;
}
::v-deep .v-data-table__wrapper th:nth-child(1),
::v-deep .v-data-table__wrapper th:nth-child(2) {
  z-index: 5 !important;
}
.theme--light ::v-deep .v-data-table__wrapper td:nth-child(1),
.theme--light ::v-deep .v-data-table__wrapper td:nth-child(2),
.theme--light ::v-deep .v-data-table__wrapper th:nth-child(1),
.theme--light ::v-deep .v-data-table__wrapper th:nth-child(2) {
  background-color: #fff !important;
}
.theme--dark ::v-deep .v-data-table__wrapper td:nth-child(1),
.theme--dark ::v-deep .v-data-table__wrapper td:nth-child(2),
.theme--dark ::v-deep .v-data-table__wrapper th:nth-child(1),
.theme--dark ::v-deep .v-data-table__wrapper th:nth-child(2) {
  background-color: #1e1e2d !important;
}
.theme--light ::v-deep .modern-row:hover td:nth-child(1),
.theme--light ::v-deep .modern-row:hover td:nth-child(2) {
  background-color: #f5f5f5 !important;
}
.theme--dark ::v-deep .modern-row:hover td:nth-child(1),
.theme--dark ::v-deep .modern-row:hover td:nth-child(2) {
  background-color: #2a2a3d !important;
}
/* Visible horizontal scrollbar */
::v-deep .v-data-table__wrapper {
  overflow-x: auto !important;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  height: 10px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 5px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border-radius: 5px;
}
::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #888;
}
.theme--dark ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #1e1e2d;
}
.theme--dark ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #444;
}
</style>
